import React, { useEffect, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Form } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import { FarmersQuery } from '~/QueryHooks/Farmers'
import { useDebounce } from 'use-lodash-debounce';
import { CollectionPointsOptionsType, ICountry } from '~/api/services/dairy'
import { useParams } from 'react-router'
import ENV from '~/environment';
import { FarmerCreateOptionsType } from '~/api/services/farmers'

interface Props {
  collectionSiteOptions?: CollectionPointsOptionsType | FarmerCreateOptionsType
  useGoogleMapLocation?: boolean
  countries: ICountry[]
}

const LocationForm: React.FC<Props> = ({ countries, useGoogleMapLocation }) => {
  const { t } = useTranslation()
  const { control, setValue, formState, formState: { dirtyFields, touchedFields }, watch } = useFormContext()
  const { id } = useParams()

  const watchCountryField = useWatch({ control, name: 'countryId' })
  const watchZipField = useWatch({ control, name: 'zip' })
  const debounceZip = useDebounce(watchZipField, 300)
  const [
    watchStreetName,
    watchStreetNumber
  ] = watch(['streetName', 'streetNumber'])

  const selectedCountry = countries?.find(country => country.id === watchCountryField)

  const {
    data: zipData,
    refetch: fetchZipData
  } = FarmersQuery.getZipDistrictState(debounceZip, dirtyFields?.zip || touchedFields.zip)

  const watchLong = useWatch({ control, name: 'longitude' })
  const watchLat = useWatch({ control, name: 'latitude' })

  const [mapLat, setMapLat] = useState(watchLat)
  const [mapLong, setMapLong] = useState(watchLong)

  useEffect(() => {
    if (watchLat != null && mapLat == null) setMapLat(parseFloat(watchLat?.toString()?.replace(',', '.')))
    if (watchLong != null && mapLong == null) setMapLong(parseFloat(watchLong?.toString()?.replace(',', '.')))
  }, [watchLat, watchLong])

  useEffect(() => {
    if (useGoogleMapLocation) {
      getLatLng(watchStreetName, watchStreetNumber, watchZipField)
    }
  }, [watchStreetName, watchStreetNumber, watchZipField, useGoogleMapLocation])

  async function getLatLng(street, number, zip) {
    const address = `${number} ${street}, ${zip}`;
    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${ENV.GOOGLE_API_KEY}`;
    try {
      const response = await fetch(url);
      const data = await response.json();

      if (data.status === 'OK') {
        const location = data.results[0].geometry.location;
        setValue('latitude', location.lat)
        setValue('longitude', location.lng)
        setMapLat(location.lat)
        setMapLong(location.lng)
      } else {
        // setError("Address not found");
      }
    } catch (err) {
      // setError("Error fetching data");
    }
  }

  useEffect(() => {
    if (debounceZip?.length >= 4 && (dirtyFields?.zip || touchedFields.zip)) {
      fetchZipData()
    }
  }, [debounceZip, dirtyFields, touchedFields, formState])

  useEffect(() => {
    if (zipData?.countryId) {
      setValue('countryId', zipData?.countryId)
      setValue('city', zipData?.name)
      setValue('cantonId', '')
    }
  }, [zipData])

  const cantonOptions = selectedCountry ? selectedCountry?.cantons?.map(state => ({ value: state.id, text: state.label })) : []

  const isValidCoordinate = (value: unknown): boolean => {
    if (value == null || value === '') return false
    const normalized = String(value).replace(',', '.')
    return !isNaN(Number(normalized))
  }

  const hasCoordinates = isValidCoordinate(mapLat) && isValidCoordinate(mapLong)

  return <>
    <Form.Group style={{ alignItems: 'end' }} widths={'equal'}>
      <FormFields.Input
        label={t('Street')}
        placeholder={t('Enter street')}
        control={control}
        rules={{
          required: {
            value: true,
            message: t('Please enter Street')
          }
        }}
        name={'streetName'}
      />
      <FormFields.Input
        label={t('Street number')}
        control={control}
        placeholder={t('Enter street number')}
        rules={{
          required: {
            value: true,
            message: t('Please enter Street number')
          }
        }}
        name={'streetNumber'}
      />
      <FormFields.Input
        label={t('Zip')}
        rules={{
          required: {
            value: true,
            message: t('Please enter Zip')
          }
        }}
        placeholder={t('Enter zip')}
        control={control}
        name={'zip'}
      />
    </Form.Group>
    <Form.Group style={{ alignItems: 'end' }} widths={'equal'}>
      <FormFields.Input
        label={t('City')}
        control={control}
        placeholder={t('Enter city')}
        rules={{
          required: {
            value: true,
            message: t('Please enter City')
          }
        }}
        styleLabel={{ left: '40%' }}
        name={'city'}
      />
      <FormFields.Select
        control={control}
        placeholder={t('Country')}
        upward={Boolean(id)}
        selectOnBlur={false}
        search
        name='countryId'
        rules={{
          required: {
            value: true,
            message: t('Please select Country')
          }
        }}
        onChange={((e, data) => {
          setValue('cantonId', '')
          setValue('countryId', data.value)
        })}
        label={t('Country')}
        options={countries?.map(country => ({ value: country.id, text: country.label, flag: country.iso }))}
      />
      {cantonOptions?.length > 0 && watchCountryField && <FormFields.Select
        control={control}
        placeholder={t('Canton')}
        upward={Boolean(id)}
        search
        selectOnBlur={false}
        noResultsMessage={t('No results found....')}
        name='cantonId'
        label={t('Canton')}
        options={cantonOptions || []}
      />}
    </Form.Group>
    <Form.Group widths={'equal'}>

    </Form.Group>
    {useGoogleMapLocation && <Form.Group widths={'equal'}>
      <FormFields.Input
        control={control}
        name={'latitude'}
        label={t('Latitude')}
        typeInput='controled'
        decimalScale={5}
        allowDecimals
        placeholder={t('Please enter latitude')}
        onBlur={() => setMapLat(parseFloat(watchLat?.toString()?.replace(',', '.')))}
      />
      <FormFields.Input
        control={control}
        name={'longitude'}
        label={t('Longitude')}
        decimalScale={5}
        typeInput='controled'
        allowDecimals
        placeholder={t('Please enter longitude')}
        onBlur={() => setMapLong(parseFloat(watchLong?.toString()?.replace(',', '.')))}
      />
    </Form.Group>}
    {/*   {hasCoordinates && useGoogleMapLocation && <GoogleMap
      zoomLevel={20}
      lat={mapLat}
      lng={mapLong}
    />} */}
  </>
}
export default LocationForm